import { QUICKMATCH_POLL_MS, type SizePref, type SpeedId } from '@kolony/shared';

// §13.8 quickmatch, the client's side: enqueue, poll until a room forms, report it.
//
// D320: the queue's presence rule has two ends, and this is the client's. A queue entry used to
// outlive its browser, because the only thing that ever removed one was the overlay's Cancel: a
// closed tab sent nothing, and the next real players in its pace and size were seated beside
// someone who never arrived. The server now drops an entry whose client stops polling (that is
// the floor, and it needs nothing from a tab that is gone); this module adds the fast path — a
// keepalive DELETE on `pagehide` while the loop runs — and the other half of the floor: a client
// whose entry was dropped while it was only FROZEN (a phone switched apps, a tab put to sleep)
// reads `idle` on its next poll and asks again with `resume`, so its wait continues instead of
// the overlay waiting forever on an entry that no longer exists. The same recovery covers a
// server restart, which empties the queue.
//
// It lives here rather than in main.ts because main.ts is coverage-excluded (D185): every
// decision below is one a test can reach.

/** The queue's status as the wire carries it (§13.8 GET /quickmatch). */
export interface QuickmatchStatus {
  state: string;
  room?: string | null;
  found?: number;
  needed?: number;
  backfillInMs?: number | null;
  widening?: boolean;
  backfill?: boolean;
  /** D288: placed in an existing public room rather than a minted table. */
  public?: boolean;
}

/** The slice of the API client the loop uses. */
export interface QuickmatchApi {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  delete<T>(path: string): Promise<T>;
}

export interface QuickmatchPorts {
  readonly api: QuickmatchApi;
  /** A request that survives the page leaving (see `keepaliveDelete`). Never throws. */
  sendOnExit(path: string): void;
  /** Where `pagehide` is heard: the window. */
  readonly page: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;
  sleep(ms: number): Promise<void>;
}

export interface QuickmatchAsk {
  guestId: string;
  size: SizePref;
  speed: SpeedId;
  /** D288: coming back from a public room — the queue restores the wait it had. */
  resume: boolean;
}

export interface QuickmatchEvents {
  waiting(status: QuickmatchStatus): void;
  formed(status: QuickmatchStatus & { room: string }): void;
  /** The first enqueue failed: nothing is queued, and nothing more will be asked. */
  unavailable(): void;
}

export interface QuickmatchRun {
  /** The player's Cancel: stop, and leave the queue. */
  cancel(): void;
}

/** The queue route for one guest (GET polls it, DELETE leaves it). */
export const quickmatchPath = (guestId: string): string => `/quickmatch?guestId=${encodeURIComponent(guestId)}`;

type ExitFetch = (url: string, init: { method: string; keepalive: boolean }) => Promise<unknown>;

/**
 * The `sendOnExit` main.ts wires. A KEEPALIVE fetch, because a plain one is cancelled when the
 * page unloads, and not `sendBeacon`, which can only POST. Best effort by design: the server's
 * presence rule is the guarantee, so a refusal or a browser without keepalive costs at most
 * `MM.staleSec` of a ghost entry, which is what the floor exists to bound.
 */
export function keepaliveDelete(fetchFn: ExitFetch, base = '/api/v1'): (path: string) => void {
  return (path) => {
    try {
      void fetchFn(base + path, { method: 'DELETE', keepalive: true }).catch(() => undefined);
    } catch {
      /* the page is leaving: there is nobody left to tell */
    }
  };
}

export function runQuickmatch(ports: QuickmatchPorts, ask: QuickmatchAsk, on: QuickmatchEvents): QuickmatchRun {
  const path = quickmatchPath(ask.guestId);
  let stopped = false;
  // Bound for the life of the loop only. NOT stopped by the event: a page that goes into the
  // back/forward cache comes back with this loop intact, reads `idle` (the DELETE landed) and
  // asks again — which is exactly right for a player who pressed Back onto a waiting screen.
  const onHide = (): void => ports.sendOnExit(path);
  ports.page.addEventListener('pagehide', onHide);
  const stop = (): void => {
    stopped = true;
    ports.page.removeEventListener('pagehide', onHide);
  };
  const enqueue = (resume: boolean): Promise<unknown> =>
    ports.api.post('/quickmatch', { guestId: ask.guestId, sizePref: ask.size, speed: ask.speed, backfill: true, ...(resume ? { resume: true } : {}) });

  const poll = async (): Promise<void> => {
    while (!stopped) {
      const res = await ports.api.get<{ status: QuickmatchStatus }>(path).catch(() => null);
      if (stopped) return; // cancelled while the request was out: report nothing more
      const status = res?.status;
      if (status?.state === 'formed' && typeof status.room === 'string' && status.room !== '') {
        stop();
        on.formed({ ...status, room: status.room });
        return;
      }
      if (status?.state === 'waiting') on.waiting(status);
      // D320: we are queued as far as this page knows, and the server holds nothing for us — it
      // dropped the entry while we were quiet, or it restarted. Ask again, resuming: the wait
      // the queue kept for us comes back, and a queue that kept nothing starts us fresh.
      else if (status?.state === 'idle') await enqueue(true).catch(() => undefined);
      await ports.sleep(QUICKMATCH_POLL_MS);
    }
  };

  void enqueue(ask.resume)
    .then(() => poll())
    .catch(() => {
      if (stopped) return;
      stop();
      on.unavailable();
    });

  return {
    cancel(): void {
      stop();
      // always sent: a POST that failed AFTER enqueueing (a room that could not be minted)
      // leaves a record only this clears
      void ports.api.delete(path).catch(() => undefined);
    },
  };
}
